<script setup>
import { computed, onMounted, ref } from 'vue';
import { formatTime } from '@chatwoot/utils';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import BarChart from 'shared/components/charts/BarChart.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const store = useStore();

const since = ref(0);
const until = ref(0);
const businessHours = ref(false);
const excludeIssueTime = ref(false);
const hideInactive = ref(false);
const errorMessage = ref('');
const latestRefreshToken = ref(0);

const agents = useMapGetter('agents/getAgents');
const vipAgentSummary = useMapGetter(
  'summaryReports/getVipAgentSummaryReports'
);
const uiFlags = useMapGetter('summaryReports/getUIFlags');

const isFetching = computed(
  () => uiFlags.value?.isFetchingVipAgentSummaryReports
);

const getMetrics = id =>
  (vipAgentSummary.value || []).find(m => m.id === Number(id)) || {};

const fmtTime = v => (v ? formatTime(v) : '--');
const fmtNum = v =>
  v === null || v === undefined ? '--' : Number(v).toLocaleString();

const rows = computed(() => {
  const agentList = (agents.value || []).map(agent => {
    const m = getMetrics(agent.id);
    return {
      id: agent.id,
      name: agent.name,
      email: agent.email,
      avatar: agent.thumbnail,
      conversations: Number(m.conversationsCount || 0),
      vipContacts: m.vipContactsCount ?? null,
      firstResponse: m.avgFirstResponseTime ?? null,
      resolutionTime: m.avgResolutionTime ?? null,
      replyTime: m.avgReplyTime ?? null,
      resolutionCount: Number(m.resolvedConversationsCount || 0),
    };
  });

  const filtered = hideInactive.value
    ? agentList.filter(r => r.conversations > 0)
    : agentList;
  return filtered.sort((a, b) => b.conversations - a.conversations);
});

const chartRows = computed(() => rows.value.slice(0, 10));

const chartCollection = computed(() => ({
  labels: chartRows.value.map(r => r.name || '--'),
  datasets: [
    {
      label: 'VIP conversations',
      data: chartRows.value.map(r => r.conversations),
      backgroundColor: '#1d75e5',
      borderRadius: 6,
      categoryPercentage: 0.72,
      barPercentage: 0.9,
    },
    {
      label: 'VIP contacts',
      data: chartRows.value.map(r => Number(r.vipContacts || 0)),
      backgroundColor: '#eab308',
      borderRadius: 6,
      categoryPercentage: 0.72,
      barPercentage: 0.9,
    },
  ],
}));

const chartOptions = {
  plugins: { legend: { display: true } },
  scales: {
    x: {
      ticks: { maxRotation: 0, minRotation: 0 },
      grid: { drawOnChartArea: false },
    },
    y: {
      beginAtZero: true,
      ticks: { callback: v => Number(v).toLocaleString() },
      grid: { drawOnChartArea: false },
    },
  },
};

const hasData = computed(() => rows.value.some(r => r.conversations > 0));

const createRefreshToken = () => {
  latestRefreshToken.value += 1;
  return latestRefreshToken.value;
};

const isLatestRefresh = token => token === latestRefreshToken.value;

const fetchAllData = async () => {
  if (!since.value && !until.value) return;
  const refreshToken = createRefreshToken();
  errorMessage.value = '';

  const [agentsResult, vipSummaryResult] = await Promise.allSettled([
    store.dispatch('agents/get'),
    store.dispatch('summaryReports/fetchVipAgentSummaryReports', {
      since: since.value,
      until: until.value,
      businessHours: businessHours.value,
      excludeIssueTime: excludeIssueTime.value,
    }),
  ]);

  if (!isLatestRefresh(refreshToken)) return;

  const agentsFailed =
    agentsResult.status === 'rejected' || agentsResult.value === false;
  const vipSummaryFailed =
    vipSummaryResult.status === 'rejected' || vipSummaryResult.value === false;

  if (agentsFailed || vipSummaryFailed) {
    errorMessage.value =
      'Failed to fetch VIP CS performance report. Please try again.';
  }
};

const onFilterChange = ({ from, to }) => {
  since.value = from;
  until.value = to;
  fetchAllData();
};

const onExcludeIssueTimeChange = () => {
  fetchAllData();
};

const onDownload = () => {
  const fileName = generateFileName({
    type: 'vip_agent',
    to: until.value,
    businessHours: businessHours.value,
  });
  store.dispatch('downloadVipAgentReports', {
    from: since.value,
    to: until.value,
    fileName,
    businessHours: businessHours.value,
    excludeIssueTime: excludeIssueTime.value,
  });
};

onMounted(() => {
  store.dispatch('agents/get');
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="va-page">
    <div class="va-shell">
      <div class="va-header">
        <div class="va-header-left">
          <h1 class="va-title">{{ $t('VIP_AGENT_REPORTS.HEADER') }}</h1>
        </div>
      </div>

      <div class="va-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          :show-inbox="false"
          @filter-change="onFilterChange"
        />
      </div>

      <div class="va-content">
        <div v-if="errorMessage" class="va-error">
          {{ errorMessage }}
        </div>

        <div class="va-detail-header">
          <h2 class="va-detail-title">VIP agent performance</h2>
          <div class="va-detail-actions">
            <label class="va-switch">
              <span
                class="va-switch-track"
                :class="{ 'va-switch-track--on': excludeIssueTime }"
              >
                <input
                  v-model="excludeIssueTime"
                  type="checkbox"
                  class="va-switch-input"
                  @change="onExcludeIssueTimeChange"
                />
                <span class="va-switch-thumb" />
              </span>
              <span>{{ $t('SUMMARY_REPORTS.EXCLUDE_ISSUE_TIME') }}</span>
            </label>

            <label class="va-switch">
              <span
                class="va-switch-track"
                :class="{ 'va-switch-track--on': hideInactive }"
              >
                <input
                  v-model="hideInactive"
                  type="checkbox"
                  class="va-switch-input"
                />
                <span class="va-switch-thumb" />
              </span>
              <span>Hide inactive agents</span>
            </label>

            <button class="va-export-btn" type="button" @click="onDownload">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        <div class="va-table-scroll">
          <table class="va-tbl">
            <thead>
              <tr>
                <th class="va-th va-th--agent va-sticky">Agent</th>
                <th class="va-th va-th--metric">VIP conversations</th>
                <th class="va-th va-th--metric">VIP contacts</th>
                <th class="va-th va-th--metric">Avg. first response</th>
                <th class="va-th va-th--metric">Avg. resolution</th>
                <th class="va-th va-th--metric">Avg. reply time</th>
                <th class="va-th va-th--metric">Resolution count</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isFetching">
                <td colspan="7" class="va-td va-td--empty">
                  <div class="va-spinner" />
                </td>
              </tr>
              <tr v-else-if="!rows.length">
                <td colspan="7" class="va-td va-td--empty">
                  No VIP agent data found.
                </td>
              </tr>
              <template v-else>
                <tr v-for="row in rows" :key="row.id" class="va-row">
                  <td class="va-td va-td--agent va-sticky">
                    <div class="va-agent">
                      <div class="va-avatar">
                        <img
                          v-if="row.avatar"
                          :src="row.avatar"
                          :alt="row.name"
                          class="va-avatar-img"
                          loading="lazy"
                        />
                        <span v-else class="va-avatar-ini">{{
                          (row.name || '?')[0]
                        }}</span>
                      </div>
                      <div class="va-agent-info">
                        <span class="va-agent-name">{{ row.name }}</span>
                        <span class="va-agent-email">{{ row.email }}</span>
                      </div>
                    </div>
                  </td>
                  <td class="va-td va-td--metric">
                    <div class="va-cell-val">
                      {{ fmtNum(row.conversations) }}
                    </div>
                  </td>
                  <td class="va-td va-td--metric">
                    <div class="va-cell-val">{{ fmtNum(row.vipContacts) }}</div>
                  </td>
                  <td class="va-td va-td--metric">
                    <div class="va-cell-val">
                      {{ fmtTime(row.firstResponse) }}
                    </div>
                  </td>
                  <td class="va-td va-td--metric">
                    <div class="va-cell-val">
                      {{ fmtTime(row.resolutionTime) }}
                    </div>
                  </td>
                  <td class="va-td va-td--metric">
                    <div class="va-cell-val">{{ fmtTime(row.replyTime) }}</div>
                  </td>
                  <td class="va-td va-td--metric">
                    <div class="va-cell-val">
                      {{ fmtNum(row.resolutionCount) }}
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <section class="va-chart-panel">
          <div class="va-chart-head">
            <h2 class="va-chart-title">VIP agent activity</h2>
            <p class="va-chart-sub">
              Top 10 agents — VIP conversations vs VIP contacts
            </p>
          </div>
          <div class="va-chart-body">
            <woot-loading-state
              v-if="isFetching"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div v-else-if="!hasData" class="va-chart-empty">
              No data available for the selected period.
            </div>
            <BarChart
              v-else
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.va-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.va-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.va-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.va-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.va-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.va-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.va-content {
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.va-error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

/* ── Detail header ── */
.va-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.va-detail-title {
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  color: #111827;
}

.va-detail-actions {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.va-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  user-select: none;
  cursor: pointer;
}

.va-switch-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.va-switch-track {
  position: relative;
  width: 27px;
  height: 20px;
  border-radius: 15px;
  background: rgb(221, 221, 221);
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.va-switch-track--on {
  background: #111827;
}

.va-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.1) 0 2px 2px 0;
  transition: left 0.2s ease-in-out;
}

.va-switch-track--on .va-switch-thumb {
  left: 10px;
}

.va-export-btn {
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.va-export-btn:hover {
  background: #f9fafb;
}

/* ── Table ── */
.va-table-scroll {
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #cfd4dc transparent;
}

.va-tbl {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  background: #ffffff;
}

.va-th {
  padding: 10px;
  text-align: left;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  color: #111827 !important;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.va-th--agent {
  min-width: 264px;
  padding-left: 8px;
}
.va-th--metric {
  min-width: 150px;
  text-align: center;
}

.va-sticky {
  position: sticky;
  left: 0;
  z-index: 5;
  background: #ffffff;
  box-shadow: 8px 0 12px -12px rgba(0, 0, 0, 0.25);
}

.va-th.va-sticky {
  z-index: 6;
}

.va-td {
  padding: 8px 10px;
  vertical-align: middle;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;
}

.va-td--agent {
  min-width: 264px;
  padding: 0 8px 0 0;
}
.va-td--metric {
  min-width: 150px;
}
.va-td--empty {
  text-align: center;
  padding: 32px;
  color: #6b7280;
}

.va-row:hover .va-td {
  background: #fafafa;
}
.va-row:hover .va-sticky {
  background: #fafafa;
}

.va-agent {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  background: transparent;
}

.va-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.va-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.va-avatar-ini {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.va-agent-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.va-agent-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.va-agent-email {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.va-cell-val {
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.va-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: va-spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes va-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Chart ── */
.va-chart-panel {
  border: 1px solid #d8dee5;
  border-radius: 4px;
  background: #ffffff;
  overflow: hidden;
}

.va-chart-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 12px 16px 8px;
  border-bottom: 1px solid #dfe3e8;
}

.va-chart-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.va-chart-sub {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.va-chart-body {
  height: 260px;
  padding: 10px 16px 16px;
}

.va-chart-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

/* ── Dark mode override ── */
::global(body.dark) .va-page,
::global(body.dark) .va-shell,
::global(body.dark) .va-header,
::global(body.dark) .va-toolbar,
::global(body.dark) .va-tbl,
::global(body.dark) .va-chart-panel,
::global(body.theme-mix) .va-page,
::global(body.theme-mix) .va-shell,
::global(body.theme-mix) .va-header,
::global(body.theme-mix) .va-toolbar,
::global(body.theme-mix) .va-tbl,
::global(body.theme-mix) .va-chart-panel {
  background: #ffffff !important;
  color: #111827 !important;
}

@media (max-width: 1100px) {
  .va-content {
    padding: 12px 16px 16px;
  }
  .va-toolbar {
    padding: 10px 16px 8px;
  }
  .va-detail-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
